import { useEffect, useMemo, useState } from "react";
import {
    Search,
    Ticket,
    Inbox,
    Clock,
    CheckCircle2,
    Loader2,
    Calendar,
    Paperclip,
    AlertCircle,
    RefreshCw,
} from "lucide-react";
import { getExpertTickets } from "../service/Vendorservisefile/suportservises.jsx";

const TICKET_STATUS_STYLES = {
    Pending: "bg-amber-100 text-amber-700",
    "In Progress": "bg-indigo-100 text-indigo-700",
    Completed: "bg-green-100 text-green-700",
};

const TICKET_STATUSES = ["All", "Pending", "In Progress", "Completed"];

const DATE_FILTERS = ["All Time", "Today", "Yesterday", "This Week", "Custom Range"];

// Maps the backend's ticket status enum to the labels this dashboard
// already uses for styling/filtering.
const API_STATUS_LABELS = {
    OPEN: "Pending",
    IN_PROGRESS: "In Progress",
    RESOLVED: "Completed",
    CLOSED: "Completed",
};

// Normalizes a raw ticket from GET /support/ticket/expert/:id into the
// shape this dashboard renders (same shape as locally-submitted tickets).
function mapApiTicket(apiTicket) {
    return {
        id: apiTicket.id,
        subject: apiTicket.subject,
        message: apiTicket.message,
        status: API_STATUS_LABELS[apiTicket.status] || apiTicket.status,
        createdAt: apiTicket.createdAt,
        category: apiTicket.category,
        priority: apiTicket.priority,
        assignedTo: apiTicket.assignedTo,
        attachments: [], // not returned by this endpoint
    };
}

// Strips time so day-based comparisons are exact regardless of the
// time-of-day portion carried in the raw ticket timestamp.
function startOfDay(date) {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
}

function endOfDay(date) {
    const d = new Date(date);
    d.setHours(23, 59, 59, 999);
    return d;
}

// Given a filter label + optional custom range, returns a [from, to] Date
// range (inclusive) to test tickets against, or null for "All Time".
function resolveDateRange(filter, customFrom, customTo) {
    const now = new Date();

    switch (filter) {
        case "Today":
            return [startOfDay(now), endOfDay(now)];
        case "Yesterday": {
            const y = new Date(now);
            y.setDate(y.getDate() - 1);
            return [startOfDay(y), endOfDay(y)];
        }
        case "This Week": {
            const from = new Date(now);
            const day = from.getDay(); // 0 = Sunday
            const diffToMonday = day === 0 ? 6 : day - 1;
            from.setDate(from.getDate() - diffToMonday);
            return [startOfDay(from), endOfDay(now)];
        }
        case "Custom Range": {
            if (!customFrom && !customTo) return null;
            const from = customFrom ? startOfDay(new Date(customFrom)) : null;
            const to = customTo ? endOfDay(new Date(customTo)) : null;
            return [from, to];
        }
        default:
            return null; // "All Time"
    }
}

// Sample tickets shown when no real tickets are available (API returned
// none, or failed) so the dashboard never looks empty during a demo.
const DUMMY_TICKETS = [
    {
        id: 100234,
        subject: "Delay in EV Battery Enclosure v2 prototype",
        message: "The 3D print job for the enclosure has been stuck in queue for 3 days, need an update on when it will start.",
        status: "Pending",
        createdAt: "2026-07-20T09:15:00",
    },
    {
        id: 100221,
        subject: "Invoice mismatch for CNC machining slot",
        message: "The invoice raised shows 4 hours of machine time but I only booked 2 hours. Please correct and resend.",
        status: "Completed",
        createdAt: "2026-07-18T14:40:00",
    },
    {
        id: 100219,
        subject: "Unable to reschedule laser cutter booking",
        message: "I'm trying to move my Friday slot to Monday but the calendar keeps throwing an error on save.",
        status: "Pending",
        createdAt: "2026-07-17T11:05:00",
    },
    {
        id: 100207,
        subject: "Access card not working at Kurnool facility",
        message: "My access card stopped working at the entrance gate since yesterday morning, had to be let in manually.",
        status: "In Progress",
        createdAt: "2026-07-15T08:50:00",
    },
    {
        id: 100195,
        subject: "Refund pending for cancelled 3D printer booking",
        message: "Cancelled my booking 10 days ago and was told refund takes 5-7 business days, still hasn't reflected.",
        status: "Completed",
        createdAt: "2026-07-10T16:20:00",
    },
    {
        id: 100188,
        subject: "Request for extended working hours access",
        message: "Our team needs facility access till 10pm during the hackathon week, how do we get this approved?",
        status: "Pending",
        createdAt: "2026-07-08T10:30:00",
    },
];

function formatTicketDate(date) {
    return new Date(date).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
}

function TicketStatCard({ icon: Icon, label, value, tone }) {
    return (
        <div className="flex items-center gap-4 rounded-2xl border border-gray-100 bg-white p-5">
            <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div>
                <p className="text-2xl font-bold text-gray-900">{value}</p>
                <p className="text-sm text-gray-500">{label}</p>
            </div>
        </div>
    );
}

export default function TicketsDashboard({ tickets }) {
    const [apiTickets, setApiTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [refreshKey, setRefreshKey] = useState(0);

    const [query, setQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    const [dateFilter, setDateFilter] = useState("All Time");
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");

    // Fetch tickets raised against expert 1 whenever the component mounts
    // (or the user hits retry after a failure).
    useEffect(() => {
        let cancelled = false;

        async function load() {
            setLoading(true);
            setError("");
            try {
                const raw = await getExpertTickets();
                if (!cancelled) setApiTickets(raw.map(mapApiTicket));
            } catch (err) {
                if (!cancelled) setError(err.message || "Failed to load your tickets.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        }

        load();
        return () => {
            cancelled = true;
        };
    }, [refreshKey]);

    // Merge tickets just submitted in this session (which may still carry
    // local-only attachments) with what the server has, de-duplicated by id.
    const data = useMemo(() => {
        const local = tickets || [];
        const localIds = new Set(local.map((t) => t.id));
        const merged = [...local, ...apiTickets.filter((t) => !localIds.has(t.id))];
        if (merged.length > 0) return merged;
        return !loading ? DUMMY_TICKETS : [];
    }, [tickets, apiTickets, loading]);

    const counts = useMemo(() => {
        return {
            total: data.length,
            pending: data.filter((t) => t.status === "Pending").length,
            inProgress: data.filter((t) => t.status === "In Progress").length,
            completed: data.filter((t) => t.status === "Completed").length,
        };
    }, [data]);

    const dateRange = useMemo(
        () => resolveDateRange(dateFilter, customFrom, customTo),
        [dateFilter, customFrom, customTo]
    );

    const filtered = useMemo(() => {
        return data.filter((t) => {
            const matchesQuery =
                query.trim() === "" ||
                t.subject.toLowerCase().includes(query.trim().toLowerCase()) ||
                t.message.toLowerCase().includes(query.trim().toLowerCase());
            const matchesStatus = statusFilter === "All" || t.status === statusFilter;

            let matchesDate = true;
            if (dateRange) {
                const [from, to] = dateRange;
                const createdAt = t.createdAt ? new Date(t.createdAt) : null;
                if (!createdAt || isNaN(createdAt)) {
                    matchesDate = false;
                } else {
                    if (from && createdAt < from) matchesDate = false;
                    if (to && createdAt > to) matchesDate = false;
                }
            }

            return matchesQuery && matchesStatus && matchesDate;
        });
    }, [data, query, statusFilter, dateRange]);

    return (
        <div>
            {/* API error banner — data still falls back to local/dummy tickets below */}
            {error && (
                <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                    <div className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 shrink-0" />
                        <span>{error}</span>
                    </div>
                    <button
                        type="button"
                        onClick={() => setRefreshKey((k) => k + 1)}
                        className="flex shrink-0 items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 transition hover:bg-red-50"
                    >
                        <RefreshCw className="h-3.5 w-3.5" /> Retry
                    </button>
                </div>
            )}

            {/* Stat cards */}
            <div className="grid grid-cols-2 gap-4 mb-6 sm:grid-cols-4">
                <TicketStatCard icon={Inbox} label="Total tickets" value={counts.total} tone="bg-indigo-50 text-indigo-500" />
                <TicketStatCard icon={Clock} label="Pending" value={counts.pending} tone="bg-amber-50 text-amber-500" />
                <TicketStatCard icon={Loader2} label="In progress" value={counts.inProgress} tone="bg-blue-50 text-blue-500" />
                <TicketStatCard icon={CheckCircle2} label="Completed" value={counts.completed} tone="bg-green-50 text-green-500" />
            </div>

            {/* Table card */}
            <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
                <div className="flex flex-col gap-4 border-b border-gray-100 p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                            <h2 className="font-bold text-gray-900">My tickets</h2>
                            <p className="mt-1 text-sm text-gray-500">
                                {loading
                                    ? "Loading your tickets..."
                                    : `${filtered.length} of ${data.length} tickets`}
                            </p>
                        </div>

                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                            <div className="relative">
                                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                                <input
                                    type="text"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Search tickets..."
                                    className="w-56 rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm text-gray-700 placeholder-gray-400 outline-none focus:border-gray-300 focus:bg-white"
                                />
                            </div>

                            <div className="relative">
                                <select
                                    value={dateFilter}
                                    onChange={(e) => setDateFilter(e.target.value)}
                                    className="appearance-none rounded-lg border border-gray-200 bg-white py-2 pl-3 pr-8 text-sm text-gray-700 outline-none focus:border-gray-300"
                                >
                                    {DATE_FILTERS.map((d) => (
                                        <option key={d} value={d}>
                                            {d}
                                        </option>
                                    ))}
                                </select>
                                <Calendar className="pointer-events-none absolute right-2 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {TICKET_STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => setStatusFilter(s)}
                                        className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${statusFilter === s
                                            ? "bg-indigo-600 text-white"
                                            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                                            }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Custom range pickers — only shown when "Custom Range" is selected */}
                    {dateFilter === "Custom Range" && (
                        <div className="flex flex-wrap items-center gap-3 rounded-lg bg-gray-50 p-3">
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-gray-500">From</label>
                                <input
                                    type="date"
                                    value={customFrom}
                                    max={customTo || undefined}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 outline-none focus:border-gray-300"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <label className="text-xs font-medium text-gray-500">To</label>
                                <input
                                    type="date"
                                    value={customTo}
                                    min={customFrom || undefined}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                    className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 outline-none focus:border-gray-300"
                                />
                            </div>
                            {(customFrom || customTo) && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setCustomFrom("");
                                        setCustomTo("");
                                    }}
                                    className="text-xs font-medium text-gray-400 underline-offset-2 hover:text-gray-600 hover:underline"
                                >
                                    Clear dates
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Ticket list */}
                {loading && data.length === 0 ? (
                    <div className="h-40 flex flex-col items-center justify-center gap-2 text-gray-400">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <p className="text-sm">Loading your tickets...</p>
                    </div>
                ) : filtered.length > 0 ? (
                    <ul className="divide-y divide-gray-100">
                        {filtered.map((t) => (
                            <li key={t.id} className="p-6 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className="font-semibold text-gray-900 truncate">{t.subject}</p>
                                        <span className="text-xs text-gray-400 shrink-0">#{t.id}</span>
                                    </div>
                                    <p className="text-sm text-gray-500 line-clamp-2">{t.message}</p>
                                    <div className="mt-2 flex flex-wrap items-center gap-3">
                                        <p className="text-xs text-gray-400">Raised on {formatTicketDate(t.createdAt)}</p>
                                        {t.attachments && t.attachments.length > 0 && (
                                            <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                                                <Paperclip className="h-3.5 w-3.5" />
                                                {t.attachments.length} attachment{t.attachments.length > 1 ? "s" : ""}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <span
                                    className={`shrink-0 inline-flex rounded-full px-3 py-1 text-xs font-medium ${TICKET_STATUS_STYLES[t.status] || "bg-gray-100 text-gray-600"
                                        }`}
                                >
                                    {t.status}
                                </span>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <div className="h-40 flex flex-col items-center justify-center gap-2 text-gray-400">
                        <Ticket className="w-8 h-8" />
                        <p className="text-sm">
                            {data.length === 0 ? "You haven't raised any tickets yet." : "No tickets match your filters."}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}